// Slug → page_type and template → page_type overrides.
//
// Precedence at runtime (see helpers.js#determinePageType):
//   1. explicit `override` argument passed by the calling component
//   2. PAGE_TYPE_OVERRIDES[slug]            (static, hand-curated)
//   3. SLUG_PAGE_TYPE_MAP[slug]             (dynamic, populated at boot from breadcrumbs.json)
//   4. URL-derived default: `${firstSegment}_page`

// SEO-doc-named slugs (01-GTM-Events-Requirements.md). Hand-maintained.
export const PAGE_TYPE_OVERRIDES = {
  'e-fatura-mukellefi-miyim': 'efatura_mukellef_form',
  'ucretsiz-demo-talep': 'ucretsiz_demo_talep',
  'fiyatlar-ana-paket': 'fiyat_listesi',
  'fiyat-listesi': 'fiyat_listesi', // live pricing slug
}

// Generic CMS pages all share the `pages/index.vue` template. Mapping them to a
// flat 'page' label loses the slug, so the analytics team saw page_type: 'page'
// everywhere. For these templates we derive page_type from the slug instead
// (e.g. /fiyat-listesi/ → 'fiyat_listesi').
const GENERIC_PAGE_TEMPLATES = new Set(['pages/index.vue'])
const slugToPageType = (slug) => slug.replace(/-/g, '_')

// Maps a Vue template path (from CMS slug payload) to a stable page_type label.
// Used to pre-populate SLUG_PAGE_TYPE_MAP from breadcrumbs.json so that clicks
// on Header/Footer fired from a blog detail page report `page_type: 'blog_page'`
// rather than the slug-derived default (`amortisman-nedir_page` etc.).
export const TEMPLATE_PAGE_TYPES = {
  'index.vue': 'home_page',
  'blog/index.vue': 'blog_page',
  'blog/detail.vue': 'blog_page',
  'blog/search.vue': 'blog_page',
  'products/product-detail.vue': 'urun_detay_page',
  'products/products-by-need.vue': 'urunler_page',
  'products/products-by-industries.vue': 'urunler_page',
  'products/products-by-size.vue': 'urunler_page',
  'products/edonusum-listing.vue': 'edonusum_page',
  'product-comparison/index.vue': 'urun_karsilastirma_page',
  'product-finder/index.vue': 'urun_bulucu_page',
  'support-announcements/index.vue': 'duyurular_page',
  'support-announcements/detail.vue': 'duyuru_detay_page',
  'support-home/index.vue': 'destek_page',
  'support-after-sales/index.vue': 'destek_page',
  'support-product-version-updates/index.vue': 'surum_guncellemeleri_page',
  'support-product-version-updates/indexv17.vue': 'surum_guncellemeleri_page',
  'support-product-version-updates/indexvHS.vue': 'surum_guncellemeleri_page',
  'support-product-version-updates/detail.vue': 'surum_guncellemeleri_page',
  'solutions/index.vue': 'cozumler_page',
  'solutions/detail.vue': 'cozum_detay_page',
  'verticals/index.vue': 'dikey_cozumler_page',
  'campaigns/index.vue': 'kampanyalar_page',
  'campaigns/detail.vue': 'kampanya_detay_page',
  'calculations/index.vue': 'hesaplama_araclari_page',
  'calculations/detail.vue': 'hesaplama_page',
  'newsroom/index.vue': 'newsroom_page',
  'newsroom/detail.vue': 'haber_detay_page',
  'pages/index.vue': 'page',
  'landing-pages/index.vue': 'landing_page',
  'about-us/customer-obsessed.vue': 'hakkimizda_page',
  'about-us/sustainability.vue': 'hakkimizda_page',
  'about-us/success-stories.vue': 'hakkimizda_page',
  'about-us/success-stories-listing.vue': 'hakkimizda_page',
  'about-us/human-resources.vue': 'insan_kaynaklari_page',
  'about-us/working-at-mikro.vue': 'insan_kaynaklari_page',
  'about-us/career-at-mikro.vue': 'insan_kaynaklari_page',
  'about-us/customer-obsessed-ceo.vue': 'hakkimizda_page',
  'why-mikro/index.vue': 'neden_mikro_page',
  'contact/index.vue': 'iletisim_page',
  'contact-us/index.vue': 'ucretsiz_demo_talep',
  'contact-us/success.vue': 'tesekkur_page',
  'legal/index.vue': 'yasal_page',
  'sitemap/index.vue': 'site_haritasi_page',
  'partners/partner-form.vue': 'is_ortaklari_page',
  'partners/partner-api-request-form.vue': 'is_ortaklari_page',
  'partners/partner-api-request-form-two.vue': 'is_ortaklari_page',
  'partners/partner-brand-identity.vue': 'is_ortaklari_page',
  'partners/partner-program.vue': 'is_ortaklari_page',
  'partners/partner-resources.vue': 'is_ortaklari_page',
  'partners/partners-listing.vue': 'is_ortaklari_page',
  'mikro-brand-identity/index.vue': 'marka_kimligi_page',
}

// Populated at app boot by the gtm.client.js plugin from breadcrumbs.json.
// Module-scoped, mutable on purpose: single browser app instance.
let SLUG_PAGE_TYPE_MAP = {}

export function setSlugPageTypeMap(map) {
  SLUG_PAGE_TYPE_MAP = map || {}
}

export function getSlugPageType(slug) {
  if (!slug) return undefined
  const normalized = slug.replace(/^\/+|\/+$/g, '')
  return SLUG_PAGE_TYPE_MAP[normalized]
}

// Builds the slug→page_type map by joining a CMS-style breadcrumbs payload
// (`[{ slug, template, title }, ...]`) with TEMPLATE_PAGE_TYPES.
export function buildSlugPageTypeMap(breadcrumbs) {
  const map = {}
  if (!Array.isArray(breadcrumbs)) return map
  breadcrumbs.forEach((entry) => {
    if (!entry || !entry.slug || !entry.template) return
    const normalized = entry.slug.replace(/^\/+|\/+$/g, '')
    if (!normalized) return
    if (GENERIC_PAGE_TEMPLATES.has(entry.template)) {
      // Generic CMS page: derive page_type from the leaf slug.
      map[normalized] = slugToPageType(normalized.split('/').pop())
      return
    }
    const pageType = TEMPLATE_PAGE_TYPES[entry.template]
    if (pageType) map[normalized] = pageType
  })
  return map
}

// Blog posts are served at the site root (e.g. /takas-ceki-nedir/), so the
// router plugin can't detect blog-detail routes by a /blog/ path prefix. We
// build the set of blog-detail slugs from the same breadcrumbs snapshot and the
// plugin tests route.path against it.
let BLOG_DETAIL_SLUGS = new Set()

export function setBlogDetailSlugs(set) {
  BLOG_DETAIL_SLUGS = set instanceof Set ? set : new Set()
}

export function isBlogDetailSlug(slug) {
  if (!slug) return false
  return BLOG_DETAIL_SLUGS.has(slug.replace(/^\/+|\/+$/g, ''))
}

export function buildBlogDetailSlugSet(breadcrumbs) {
  const set = new Set()
  if (!Array.isArray(breadcrumbs)) return set
  breadcrumbs.forEach((entry) => {
    if (!entry || !entry.slug || entry.template !== 'blog/detail.vue') return
    const normalized = entry.slug.replace(/^\/+|\/+$/g, '')
    if (normalized) set.add(normalized)
  })
  return set
}
